/**
 * Ionlar jadvali: zaryad, molyar massa, eritmadagi rang (ko'rinadigan bo'lsa).
 * Rang [r,g,b] — konsentratsiyaga qarab UI aralashtiradi (`colorConc` — to'liq rang beradigan
 * konsentratsiya, mol/L; kuchli bo'yovchi ionlar uchun kichik).
 * Manba: umumiy kimyo ma'lumotnomalari (Glinka; Xomchenko), rang — sifat tahlili jadvallari.
 * Kompleks ionlar (FeSCN²⁺, [Cu(NH₃)₄]²⁺, [Ag(NH₃)₂]⁺, [Al(OH)₄]⁻, [Zn(OH)₄]²⁻) alohida ion sifatida.
 */
export interface Ion {
  id: string;
  formula: string;
  charge: number;
  /** kg/mol */
  molarMass: number;
  /** Eritma rangi [r, g, b], null — rangsiz */
  color: [number, number, number] | null;
  /** To'liq rang beradigan konsentratsiya, mol/L (standart 0,05) */
  colorConc?: number;
}

const I = (id: string, formula: string, charge: number, molarMass: number, color: [number, number, number] | null = null, colorConc?: number): Ion => ({
  id,
  formula,
  charge,
  molarMass,
  color,
  ...(colorConc !== undefined ? { colorConc } : {}),
});

export const IONS: Record<string, Ion> = {
  // Kationlar
  H: I('H', 'H⁺', 1, 0.001008),
  Li: I('Li', 'Li⁺', 1, 0.006941),
  Na: I('Na', 'Na⁺', 1, 0.02299),
  K: I('K', 'K⁺', 1, 0.039098),
  Cs: I('Cs', 'Cs⁺', 1, 0.132905),
  NH4: I('NH4', 'NH₄⁺', 1, 0.018039),
  Ag: I('Ag', 'Ag⁺', 1, 0.107868),
  Ca: I('Ca', 'Ca²⁺', 2, 0.040078),
  Mg: I('Mg', 'Mg²⁺', 2, 0.024305),
  Sr: I('Sr', 'Sr²⁺', 2, 0.08762),
  Ba: I('Ba', 'Ba²⁺', 2, 0.137327),
  Zn: I('Zn', 'Zn²⁺', 2, 0.06538),
  Mn: I('Mn', 'Mn²⁺', 2, 0.054938, [245, 205, 215], 0.5),
  Fe2: I('Fe2', 'Fe²⁺', 2, 0.055845, [170, 215, 170], 0.3),
  Cu: I('Cu', 'Cu²⁺', 2, 0.063546, [60, 140, 230], 0.08),
  Ni: I('Ni', 'Ni²⁺', 2, 0.058693, [80, 190, 120], 0.1),
  Pb: I('Pb', 'Pb²⁺', 2, 0.2072),
  Al: I('Al', 'Al³⁺', 3, 0.026982),
  Fe3: I('Fe3', 'Fe³⁺', 3, 0.055845, [210, 150, 50], 0.06),
  Cr3: I('Cr3', 'Cr³⁺', 3, 0.051996, [70, 140, 90], 0.05),
  // Kompleks kationlar
  FeSCN: I('FeSCN', '[Fe(SCN)]²⁺', 2, 0.113928, [170, 10, 20], 0.004),
  CuNH3: I('CuNH3', '[Cu(NH₃)₄]²⁺', 2, 0.13367, [25, 50, 200], 0.02),
  AgNH3: I('AgNH3', '[Ag(NH₃)₂]⁺', 1, 0.14193),
  /** Biuret kompleksi (oqsil + Cu²⁺, ishqoriy) — binafsha */
  CuProt: I('CuProt', 'Cu²⁺·oqsil', 2, 0.063546, [125, 50, 170], 0.01),
  /** Glyukoza/glitserin bilan Cu(II) kompleksi — to'q ko'k */
  CuPoly: I('CuPoly', 'Cu²⁺·poliol', 2, 0.063546, [30, 70, 200], 0.015),
  // Anionlar
  OH: I('OH', 'OH⁻', -1, 0.017007),
  Cl: I('Cl', 'Cl⁻', -1, 0.035453),
  Br: I('Br', 'Br⁻', -1, 0.079904),
  I: I('I', 'I⁻', -1, 0.126904),
  NO3: I('NO3', 'NO₃⁻', -1, 0.062004),
  SO4: I('SO4', 'SO₄²⁻', -2, 0.096063),
  SO3: I('SO3', 'SO₃²⁻', -2, 0.080063),
  HSO3: I('HSO3', 'HSO₃⁻', -1, 0.081071),
  S2O3: I('S2O3', 'S₂O₃²⁻', -2, 0.112128),
  S: I('S', 'S²⁻', -2, 0.032065),
  HS: I('HS', 'HS⁻', -1, 0.033073),
  CO3: I('CO3', 'CO₃²⁻', -2, 0.060009),
  HCO3: I('HCO3', 'HCO₃⁻', -1, 0.061017),
  PO4: I('PO4', 'PO₄³⁻', -3, 0.094971),
  H2PO4: I('H2PO4', 'H₂PO₄⁻', -1, 0.096987),
  SiO3: I('SiO3', 'SiO₃²⁻', -2, 0.076083),
  CH3COO: I('CH3COO', 'CH₃COO⁻', -1, 0.059044),
  SCN: I('SCN', 'SCN⁻', -1, 0.058082),
  FeCN6: I('FeCN6', '[Fe(CN)₆]⁴⁻', -4, 0.211949, [235, 225, 140], 0.3),
  ClO: I('ClO', 'ClO⁻', -1, 0.051452),
  ClO3: I('ClO3', 'ClO₃⁻', -1, 0.083451),
  MnO4: I('MnO4', 'MnO₄⁻', -1, 0.118936, [140, 30, 160], 0.004),
  MnO4g: I('MnO4g', 'MnO₄²⁻', -2, 0.118936, [40, 140, 60], 0.01),
  Cr2O7: I('Cr2O7', 'Cr₂O₇²⁻', -2, 0.215988, [235, 130, 30], 0.02),
  S4O6: I('S4O6', 'S₄O₆²⁻', -2, 0.224256),
  BrO: I('BrO', 'BrO⁻', -1, 0.095903),
  IO: I('IO', 'IO⁻', -1, 0.142903),
  H2BO3: I('H2BO3', 'H₂BO₃⁻', -1, 0.060826),
  St: I('St', 'C₁₇H₃₅COO⁻', -1, 0.283471),
  AlOH4: I('AlOH4', '[Al(OH)₄]⁻', -1, 0.095011),
  ZnOH4: I('ZnOH4', '[Zn(OH)₄]²⁻', -2, 0.133408),
};

export function ion(id: string): Ion {
  const i = IONS[id];
  if (!i) throw new Error(`Unknown ion: ${id}`);
  return i;
}

/** Kation (musbat zaryad) — zaryad jadvaldan */
export function isCationId(id: string): boolean {
  return (IONS[id]?.charge ?? 0) > 0;
}
